import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Camera, Check, ChevronDown, ChevronRight, Grid2x2, HardHat, Loader2, RefreshCw, Search, VideoOff,
} from 'lucide-react';

import { getLabourForDay, type LabourDayDto, type LabourSessionDto } from '@/api/client';
import { WorkTruckMark, WORK_MARK_SPIN_SECONDS } from '@/components/WorkTruckMark';
import { PauseMark } from '@/components/PauseMark';
import { labourLook, pausedSince, workedLabel, agoLabel } from '@/lib/labourState';
import { SECTION_HEADER } from '@/lib/utils';
import {
    labourMatchesFocus, focusBasisLabel, focusScopeLabel, focusBlockedReason, type RailFocus,
} from '@/lib/railFocus';
import { onBoard, boardScopeNote } from '@/lib/boardScope';
import { useRailSlotClose } from '@/lib/railSlot';
import { useClickIntent, type ClickIntent } from '@/lib/clickIntent';
import { LabourCorrectionDialog } from '@/components/LabourCorrectionDialog';

/** The hover viewer's size, and the gap it keeps from the icon it belongs to. */
const PREVIEW_W = 320;
const PREVIEW_H = 200;
/** ⭐ The wall view is wider — it is showing several feeds, not one. */
const WALL_W = 380;
const WALL_H = 260;
const GAP = 8;


/**
 * LIVE LABOR — a breakout under Driver Check-Ins (#143).
 *
 * Pete, 2026-08-24: *"The scheduler will also need to see an additional way on their
 * main screen to see the labor in progress"*.
 *
 * ---- ⭐ THE DOOR LEADS. THAT IS THE WHOLE LAYOUT. ---------------------------
 *
 * Pete, 2026-08-25: *"Dock Door needs to be on the top in bold with a dash after and
 * the labor name. The scheduler is checking the status of the dock doors via where the
 * labor stands."*
 *
 * ⛔ SO THIS IS NOT A LIST OF WORKERS, IT IS A LIST OF DOORS. The question being asked
 * is "what is happening at door 4", and the worker is the answer to it — not the other
 * way round. Reading the panel top-to-bottom should scan as a column of door numbers.
 *
 * ⚠️ A SESSION WITH NO DOOR STILL SHOWS ITS SLOT, reading "no door". Blanking it would
 * make the column of doors ragged and, worse, would hide the one case a supervisor
 * actually needs to chase — somebody clocked onto a truck that is not at a door.
 *
 * ---- ⭐ "LABOR", NOT "LABOUR" ----------------------------------------------
 *
 * Pete, 2026-08-25. Symbia is American and the screen should be. ⚠️ THE IDENTIFIERS IN
 * THIS FILE ARE STILL BRITISH — `LabourPanel`, `getLabourForDay`, `LabourSessionDto`.
 * That is deliberate for now: renaming them reaches the API client, the route, the
 * repository AND the markers in deploy-backend-paperwork.ps1 and verify-deployed.ps1,
 * so it is a backend deploy rather than a wording change. The USER never sees them.
 * `dbo.LaborSession` and `LaborPulse` were already American; the split is untidy and
 * tracked separately.
 *
 * ⛔ DOCK RENDERS; PIPELINE OWNS. Nothing here starts, stops or edits a session.
 */

interface LabourPanelProps {
    facilityCode: string;
    /** Facility-local day. The server scopes to local midnight-to-midnight. */
    date: string;
    getToken: () => Promise<string>;
    /** Bumped by the parent's Refresh so labor reloads with the rest of the panel. */
    refreshKey?: number;
    /** ⭐ Cross-highlight with the grid, same as a check-in card. */
    /**
     * ⛔ THE SECOND ARGUMENT IS THE POINT. A rail row and a grid chip both report an
     * appointment hover, and the page must treat them oppositely: a grid chip
     * populates the panels, a RAIL row only marks the board. Without an origin the
     * panels re-scope, which replaces the very row the cursor is on.
     *   ** A HOVER THAT RE-SCOPES THE LIST CONTAINING THE HOVERED ROW IS A LOOP BY
     *      CONSTRUCTION. ** (Pete, 2026-09-21)
     */
    onHoverAppointment?: (id: string | null, origin?: 'grid' | 'rail') => void;
    /**
     * ---- ⭐⭐⭐ SINGLE CLICK LATCHES (Pete, 2026-09-27) ----------------------
     *
     * > *"Labor is not working on the hover or the single click btw. Hover should
     * > narrow down the calendar, single click makes it stay."*
     *
     * ⛔⛔ IT WAS NEVER BUILT. Not broken, not regressed — this panel has never had a
     * click handler on a row and never received a prop to call. Hover reported an id
     * for the ring; nothing ever reported a choice.
     *   ** "IT DOESN'T WORK" AND "IT WAS NEVER WIRED" LOOK IDENTICAL FROM THE FLOOR,
     *      AND ONLY ONE OF THEM IS A REGRESSION. **
     *
     * ⭐ IT SENDS AN **ID**, NOT A FOCUS. This panel holds `LabourSessionDto`s and has
     * no `AppointmentDto` to build one from; `CheckInPanel` has the day's list and
     * builds it there, so the label, the driver join and the identifiers are computed
     * once for all three rails instead of three times.
     *   ** THE COMPONENT THAT HOLDS THE DATA BUILDS THE VALUE. A CHILD THAT ONLY HAS
     *      AN ID SHOULD SEND AN ID. **
     */
    onChooseAppointment?: (id: string | null) => void;
    /**
     * ⭐ THE LATCHED RAIL SCOPE. Pete, 2026-09-10: *"lets do the same thing for
     * labor panel. Each driver, appointment and then a ALL icon to see all the
     * labor again."*
     *
     * ⛔ LABOUR SCOPES ONLY THROUGH THE LOAD — `workerName` is the WAREHOUSE
     * worker, not the driver, so an unmatched arrival has no labour view and the
     * panel says so rather than showing an empty list. See `railFocus`.
     */
    focus?: RailFocus | null;
    /**
     * ---- ⭐⭐ WHAT THE BOARD IS SHOWING (Pete, 2026-09-25) ------------------
     * > *"it doesn't filter the drivers/client/carrier/consignee and the right
     * > side panels to those appointments"* -> *"same with all the status' too"*.
     * ⛔ A SECOND AXIS, NOT A NARROWER FOCUS. `focus` is a hover or a selection;
     * this is the board's filter set. Folding one into the other would make a
     * mouse-over change what the filters mean. See `lib/boardScope.ts`.
     * ⚠️ `undefined` MEANS NO BOUND. An empty Set is a real answer.
     */
    boardApptIds?: ReadonlySet<string>;
    onClearFocus?: () => void;
}

/** ⚠️ Ticks so an open session's elapsed time is live rather than as-at-load. */
function useNow(activeMs: number | null) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (activeMs === null) return;
        const t = setInterval(() => setNow(Date.now()), activeMs);
        return () => clearInterval(t);
    }, [activeMs]);
    return now;
}

function hhmm(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * ⚠️ MINUTES, NOT "2h 5m 13s". A dock supervisor is comparing durations at a glance,
 * and seconds churn without ever changing a decision.
 */
function elapsedLabel(startedUtc: string, finishedUtc: string | null, nowMs: number): string {
    const end = finishedUtc ? new Date(finishedUtc).getTime() : nowMs;
    const mins = Math.max(0, Math.round((end - new Date(startedUtc).getTime()) / 60000));
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

export function LabourPanel({
    facilityCode, date, getToken, refreshKey, onHoverAppointment, onChooseAppointment, boardApptIds,
    /**
     * ⛔⤴ ~~`onClearFocus`~~ NO LONGER DESTRUCTURED (increment 3, 2026-09-19). The
     * per-panel `ALL` button is gone; ONE master reset at the top of the rail clears
     * the focus for all of them. ⚠️ The PROP stays in the interface so the page's
     * wiring is unchanged and so a panel can regain its own control without a
     * signature change — but nothing here may render one, or there would be five
     * controls for one action again.
     */
    focus = null,
}: LabourPanelProps) {
    /**
     * ⛔⛔ COLLAPSED BY DEFAULT (2026-08-27). Pete: *"Lets make the Sliders all collapsed
     * by default with that arrow on the left side except driver checkins. So the user has
     * to expand the arrow to see other items."*
     *
     * ⚠️ THE RAIL HAS BEEN LOSING A FIGHT FOR HEIGHT ALL DAY — the phone line and the
     * Needs-license checkbox (#310) were both moved to claw back single lines. Three
     * sections open at once is the real cause: the check-ins, which are the reason the
     * rail exists, get whatever is left over.
     *
     * ⭐ SO THE DEFAULT IS NOW "one row of chrome", and each section carries its counts
     * IN ITS HEADER — which is why folding them costs nothing: a folded Communications
     * still says how many failed, and a folded Labor still says how many are in progress.
     * ⛔ Sections whose headers were silent could not have been folded this way.
     *
     * ⚠️ DRIVER CHECK-INS ARE NOT ONE OF THESE and stay expanded. They are the panel.
     */
    const [open, setOpen] = useState(false);

    /* ⭐ TELL THE RAIL. One line, no prop contracts touched — see
       `lib/railSlot.ts` for why this reports upward instead of being lifted.
       ⚠️ The rail uses it to give an OPEN card the leftover space and to move
       it above the closed ones; a card that never reported would simply keep
       today's behaviour rather than break. */
    /**
     * ⛔⛔ ~~THE ACCORDION~~ REVERTED 2026-09-10, SAME DAY, BY PETE: *"now they
     * don't work. none of them expand. Put it back."*
     *
     * ⚠️ THE FAULT WAS A RENDER/COMMIT RACE, not the idea. Clicking a chevron set
     * `open = true`; on THAT render the rail had not been told yet, so `openCodes`
     * was still empty and the yield signal read TRUE for the panel that had just
     * opened. Both effects then ran in the same commit — the report went up and the
     * panel closed itself. It closed on the way open, every time.
     *
     * ⛔⛔ AND THE SIMULATION THAT PASSED IT TESTED THE WRONG THING. It modelled
     * report → reduce → read as a settle-to-fixpoint loop and converged in two
     * passes. React captures the yield value during RENDER, before the report has
     * updated the context — the simulation had no render/commit ordering in it at
     * all, so it could not have caught this. §4.14, in the check written to prove
     * the accordion safe.
     *
     * ⭐ WHEN IT COMES BACK: report SYNCHRONOUSLY from the click, alongside
     * `setOpen`, so the next render already has the new `openCodes` and the stale
     * window does not exist. An effect is the wrong place for it.
     */
    /* ⭐ DECISION 193 — the two-panel cap. `useRailSlotClose` reports this panel's
       state upward AND closes it when the rail says a third panel pushed it out.
       ⛔ The policy is `nextRailSlots` in lib/railSlot.ts, proven by
       tools/rail-slot-test.cjs — not reasoned about here. */
    useRailSlotClose('LABOUR', open, setOpen);

    /** ⭐ #322 — the door picked in the filter row, '' meaning every door. */
    const [doorFilter, setDoorFilter] = useState('');

    /**
     * ⭐ THE FILTER ROW IS BEHIND THE SEARCH ICON NOW — the same shape Driver
     * Check-ins uses (Pete, 2026-09-09: *"labor should now have the same think -
     * search icon that expands the all doors line (saving space)"*).
     */
    const [showFilters, setShowFilters] = useState(false);

    const [filter, setFilter] = useState('');

    /**
     * ⛔⛔ IS A FILTER ON RIGHT NOW? This drives the amber dot, and the dot is the
     * whole reason hiding the row is safe: a filter that is on while the row is
     * CLOSED is invisible, and an invisible filter makes a short list look like a
     * quiet day. ⚠️ The "N hidden" line below stays outside the toggle for the
     * same reason - two independent ways to notice, because this one matters.
     *
     * ⛔⛔ IT MUST STAY **BELOW** BOTH `filter` AND `doorFilter`. It read them one
     * line before `filter` was declared and the build stopped with TS2448 -
     * `const` is block-scoped and in its temporal dead zone until its own line.
     * ⚠️ AND NOTHING LOCAL CAUGHT IT: an esbuild PARSE accepts this happily, so
     * the only check that sees it is `tsc`. §4.12 - a parse is not a type-check.
     */
    const filtersActive = filter.trim().length > 0 || doorFilter !== '';
    const [data, setData] = useState<LabourDayDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * ---- ⭐ F131 — DOUBLE-CLICK A ROW TO CORRECT IT (Pete, 2026-09-29) --------
     *
     * > *"For labor, should open an edit screen on that labor event so items can be
     * > changed?"*
     *
     * ⛔ THE SINGLE CLICK WAITS, like every other rail card (F130, `lib/clickIntent`):
     * a click latches the scope, and without the wait the first click of a
     * double-click would re-lay the board before the second one arrived.
     */
    const clickIntent = useClickIntent();
    const [correcting, setCorrecting] = useState<LabourSessionDto | null>(null);


    /**
     * ---- ⭐⭐⭐ #68 — ONE APPOINTMENT MEANS **ALL** OF ITS ROWS (2026-09-22) ----
     *
     * > Pete, 2026-09-21: *"When singling out an appointment, it should always show
     * > all the labor, communications, paperwork, history on that appointment -
     * > looks like its filtered on time or something."*
     *
     * ⛔⛤ IT WAS NOT THIS PANEL FILTERING. The route returned a window and the rows
     * outside it were never sent, so no amount of client-side filtering could have
     * shown them.
     *   ** A PANEL THAT FILTERS WHAT IT RECEIVED CANNOT SHOW WHAT IT WAS NOT SENT,
     *      AND FROM THE OUTSIDE THE TWO LOOK IDENTICAL. **
     *
     * ⛔⛔ ONLY WHEN THE FOCUS NAMES EXACTLY ONE LOAD. `client`, `carrier` and
     * `consignee` cover every appointment that party has today (decision 188), and
     * sending one of their ids would silently narrow the panel to a single truck —
     * a worse answer than the window, and one that looks correct.
     * ⚠️ A `driver` focus counts only once he is MATCHED. An unmatched driver has a
     * null appointment and is scoped by the phone tail against what is on screen,
     * which still needs the window.
     *   ** A NARROWING THAT LOOKS LIKE AN ANSWER IS WORSE THAN NO NARROWING. **
     */
    const scopeAppointmentId = focus
        && (focus.kind === 'appointment' || focus.kind === 'driver')
        ? focus.appointmentId
        : null;

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            /* ⭐ #68. Null = the day, exactly as before. */
            setData(await getLabourForDay(await getToken(), facilityCode, date, scopeAppointmentId));
        } catch (err) {
            /**
             * ⛔ AN ERROR IS SHOWN, NOT SWALLOWED. Labor is supplementary and it would
             * be easy to hide a failure and render nothing — but "nobody is working"
             * and "we could not ask" look identical on this panel, and only one of them
             * means somebody should go and look at the floor.
             *
             * ⭐ THIS EARNED ITS KEEP ON 2026-08-25, within minutes of going live: it
             * surfaced "SELECT permission was denied on the object 'TaskType'", which
             * named the exact missing database grant. A silent empty panel would have
             * read as a quiet day.
             */
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
        /* ⛔ THE SCOPE IS A DEPENDENCY — see the note above `load`. */
    }, [facilityCode, date, getToken, scopeAppointmentId]);

    useEffect(() => { void load(); }, [load, refreshKey]);

    /**
     * ⚠️ POLLS ONLY WHILE SOMEBODY IS ACTUALLY WORKING, and only while the section is
     * open. A closed panel on a finished day should cost nothing.
     */
    const anyOpen = (data?.working ?? 0) > 0;
    useEffect(() => {
        if (!open || !anyOpen) return;
        const t = setInterval(() => { void load(); }, 60_000);
        return () => clearInterval(t);
    }, [open, anyOpen, load]);

    const now = useNow(open && anyOpen ? 30_000 : null);
    const sessions = data?.sessions ?? [];

    /**
     * ⭐⭐ THE FILTERS (2026-08-27). Pete: *"lets put checkbox filters on Labor slider
     * and Communication Slider so we can narrow down/filter. Labor should have dock
     * door."*
     *
     * ⭐ THE DOOR IS THE RIGHT AXIS HERE, unlike on Communications. Labour happens AT
     * a door — `doorName` is on every session — and *"what is happening on door 12"*
     * is the question a supervisor walks up to this panel with.
     *
     * ⛔ THE LIST IS BUILT FROM THE DOORS THAT HAVE WORK TODAY, not from every door
     * at the site. A dropdown of forty doors where three have sessions is a control
     * that hides its own answer, and picking an empty one looks like a broken panel.
     */
    const doors = [...new Set(sessions.map(s => s.doorName).filter((d): d is string => !!d))].sort();

    /**
     * ⚠️ ONE BOX FOR EVERYTHING ELSE — worker, load, client, carrier. Same reasoning
     * as the Communications panel: a supervisor types the thing they know, and
     * making them first pick WHICH FIELD they know is a question they should not
     * have to answer.
     */
    /* ⭐ THE HOVER SCOPE FIRST, THE TYPED FILTER SECOND — the same separation
       Communications uses, so "N hidden by the filter" keeps counting only what the
       filter hid. See the long note there. */
    const blockedReason = focus ? focusBlockedReason(focus, 'labour') : null;
    const focused = !focus
        ? sessions
        : (blockedReason ? [] : sessions.filter(s => labourMatchesFocus(s, focus)));
    /**
     * ---- ⭐⭐ THE BOARD'S BOUND, BETWEEN THE FOCUS AND THE TYPED FILTER ------
     *
     * ⛔ ORDER MATTERS AND IT IS NOT ARBITRARY. `hidden` below means "hidden by
     * the TYPED filter" and the comment above guards that meaning on purpose.
     * Applying the board bound after it would fold two different causes into one
     * number and the Clear button would stop being able to fix it.
     *   ** A COUNT OF WHAT A CONTROL HID MUST NOT INCLUDE WHAT A DIFFERENT
     *      CONTROL HID, OR ITS "CLEAR" IS A BUTTON THAT DOES NOT WORK. **
     * ⚠️ A SESSION WITH NO APPOINTMENT SURVIVES — see `lib/boardScope.ts`.
     */
    const inScope = focused.filter(s => onBoard(boardApptIds, s.appointmentId));
    const boardHidden = focused.length - inScope.length;

    const needle = filter.trim().toLowerCase();
    const visible = inScope.filter(s => {
        if (doorFilter && s.doorName !== doorFilter) return false;
        if (!needle) return true;
        return [s.workerName, s.appointmentNumber, s.clientName, s.carrierName, s.taskLabel, s.doorName]
            .filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
    /** ⛔ Never a silently shorter list — see the note on the row below. */
    const hidden = inScope.length - visible.length;

    return (
        <div className="flex min-h-0 flex-col border-t">
            {/**
              * ⛔ A DIV WITH TWO BUTTONS, NOT ONE BUTTON (2026-08-27).
              *
              * Pete asked for a Refresh here to match Scanned Paperwork. ⚠️ THE HEADER
              * WAS A SINGLE <button>, and a button inside a button is invalid HTML —
              * the browser hoists it out and the inner click is swallowed, so Refresh
              * would render and do nothing. Same shape CommunicationsPanel already uses.
              *
              * ⭐ STICKY — see ScanFolderPanel. The header carries the In progress /
              * Finished counts, which are the reason to look.
              */}
                {/**
                  * ⭐⭐ THE HEADER IS A SIBLING OF THE SCROLLER, NOT INSIDE IT (2026-08-27).
                  *
                  * Pete: *"when you scroll those sections, the 'Scanned Paperwork' title
                  * bar should not disappear."*
                  *
                  * ⛔ IT WAS ALREADY `sticky top-0` AND THAT WAS THE PROBLEM — sticky is a
                  * promise about a scroll container, and this rail has three of them
                  * nested inside each other. A sticky header pins to whichever ancestor
                  * happens to be scrolling, so it holds until a wheel event chains to a
                  * DIFFERENT one and then rides away with it.
                  *
                  * ⭐ A HEADER THAT IS NOT INSIDE ANY SCROLLER CANNOT SCROLL AWAY. The
                  * root is now a flex column, the header is `shrink-0`, and only the BODY
                  * below it scrolls. That is a structural guarantee rather than a CSS
                  * promise, and it does not care how many scrollers surround it.
                  *
                  * ⚠️ `sticky` WAS REMOVED RATHER THAN LEFT IN "just in case". A sticky
                  * that no longer does anything is a line that will be trusted by the next
                  * person to read it.
                  */}
            <div className={`shrink-0 bg-card flex w-full items-center gap-2 px-3 py-2 ${SECTION_HEADER}`}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                className="flex min-w-0 items-center gap-2 text-left"
            >
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {/* ⭐ PURPLE, TO MATCH COMMUNICATIONS IN EMERALD (Pete, 2026-09-08:
                    *"lets make the Labor icon purple like the communications icon is
                    green. Want consistency."*). ⚠️ A LITERAL CLASS, never built from a
                    variable — Tailwind scans source text, and a composed class name is
                    one it never generates, so the icon would inherit its colour and look
                    merely wrong rather than broken. */}
                <HardHat className="h-3.5 w-3.5 text-purple-600" />
                {/* ⭐ THE NAME WEARS THE ICON'S COLOUR (Pete, 2026-09-10): *"lets try
                    having the panel name be the same color as the icon … the word label
                    will be purple to match the icon color for labor."* ⛔ One colour per
                    panel, matched to its glyph — see the note in `PaperworkSliver`. */}
                <span className="text-sm font-medium text-purple-600">Labor</span>
                {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            </button>
            {/**
              * ---- ⭐⭐ THE SEARCH ICON, MATCHING DRIVER CHECK-INS -----------------
              *
              * > Pete, 2026-09-09: *"labor should now have the same think - search
              * > icon that expands the all doors line (saving space)"*
              *
              * ⚠️ IT IS OUTSIDE THE TITLE BUTTON. A `<button>` inside a `<button>` is
              * invalid HTML — the browser closes the outer one early and this control
              * is silently unclickable, or clicking it also folds the panel.
              *
              * ⛔ AND IT IS SHOWN EVEN WHEN THE ROW BEHIND IT WOULD BE EMPTY. The row
              * only renders above three sessions; an icon that appeared and vanished
              * as the day filled up would read as a glitch, and its `title` says which
              * case it is rather than leaving a dead control unexplained.
              */}
            <button
                type="button"
                onClick={() => {
                    /**
                     * ---- ⛔⛔ IT OPENS THE PANEL TOO (Pete, 2026-09-09) ------
                     *
                     * > *"When you click the search button on the labor and comm
                     * > panels and the panels are collapsed, it does nothing.
                     * > should open the panel and show the search together"*
                     *
                     * ⚠️ THE STRIP LIVES INSIDE `{open && (...)}`, so toggling
                     * `showFilters` on a FOLDED card set a flag nobody could see.
                     * The click registered, the state changed, and the screen did
                     * not move - a control that does nothing, which is the exact
                     * failure this codebase keeps finding green.
                     *
                     * ⛔ OPENING ONLY, NEVER CLOSING. Hiding the strip must not
                     * fold the whole panel: a scheduler who has finished
                     * filtering still wants the list they were filtering.
                     */
                    const next = !showFilters;
                    setShowFilters(next);
                    if (next) setOpen(true);
                }}
                aria-expanded={showFilters}
                aria-label={showFilters ? 'Hide search and filters' : 'Show search and filters'}
                title={filtersActive && !showFilters
                    ? 'Filters are on - open to see or clear them'
                    : sessions.length <= 3
                        ? 'Nothing to narrow yet - the list is short'
                        : showFilters ? 'Hide search and filters' : 'Search and filter'}
                className="relative shrink-0 self-center rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
                <Search className="h-3.5 w-3.5" />
                {filtersActive && (
                    <span
                        aria-hidden="true"
                        className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-amber-500"
                    />
                )}
            </button>
            {/* ⛔ INCREMENT 3 — A PLAIN LABEL, NOT A BUTTON. The five per-panel `ALL`
            buttons are gone; one master reset at the top of the rail
            replaces them. ** A LABEL IS AN INDICATOR AND MUST READ IN
            EVERY STATE; A RESET IS A CONTROL AND MUST NOT BE ON SCREEN
            WHEN IT WOULD DO NOTHING. **
            ⚠️ Measured before the change: all four panels already share
            ONE focus, so every `ALL` already reset all four -- five
            controls for one action, and nothing said so.
            ⭐ MOVED BELOW THE SEARCH ICON 2026-09-19 — Pete: *"the search icon and the
            (client) are all inconsistent on the panels. They should all be like
            Communications. Search Icon, the client then total"*. The order is
            **name · 🔍 · (scope) · count · ↻** in all five rails. */}
            <span
                title={focus ? `Showing only ${focusBasisLabel(focus)}` : 'Showing everything today'}
                className="shrink-0 self-center text-[10px] leading-none text-muted-foreground"
            >
                {focusScopeLabel(focus)}
            </span>
            {/**
              * ---- ⭐⭐ THE COUNTS SIT AFTER THE SEARCH ICON, NOT INSIDE THE TITLE ---
              *
              * > Pete, 2026-09-09: *"Put the search button in the wrong spot, not
              * > consistent with Communications or Driver Checkins. Should be right
              * > of the word 'Labor'"*
              *
              * ⛔⛔ THE CAUSE WAS `ml-auto` ON THE COUNTS **INSIDE** THE TITLE BUTTON.
              * A search icon cannot go inside that button — a `<button>` inside a
              * `<button>` is invalid HTML — so it could only be appended AFTER it, and
              * the counts had already eaten the whole row on their way to the right
              * edge. The icon therefore landed past "Finished 5" instead of beside
              * "Labor", which is nowhere near where the other two panels put it.
              *
              * ⭐ SO THE COUNTS MOVED OUT and carry the `ml-auto` here, one level up.
              * Title + icon stay left; counts and refresh sit right. ⚠️ THE SAME SHAPE
              * AS Communications AND Driver Check-ins, which is the whole request —
              * three rails in one column that put their controls in three places is a
              * screen nobody can scan.
              *
              * ⛔ AND THE TITLE BUTTON MUST NOT REGAIN `flex-1`. It would take the
              * space `ml-auto` needs and silently push the icon right again.
              *
              * ⭐ BOTH COUNTS ARE STILL SHOWN, INCLUDING AT ZERO — a missing
              * "In progress" could mean "nobody is" or "the panel forgot", and a
              * supervisor cannot tell those apart. ⚠️ AND THE WORDS STILL MATCH THE
              * CARDS: the header used to say Working/Done while the rows said
              * something else, and two vocabularies for one fact is how a screen stops
              * being scannable.
              */}
            {/**
              * ---- ⛔ ~~`In progress n · Finished n`~~ — SUPPRESSED 2026-09-19 (§0.3) --
              *
              * ⭐ Pete: *"In-Progress and Finished is too much on that panel right now -
              * can you suppress those for now - maybe we turn them on later"*.
              *
              * ⚠️ STRUCK WHERE IT LIVES, NOT DELETED, because the reasoning above it is
              * still correct and re-deriving it would cost more than the markup does.
              * ✅ RESTORING IT IS DELETING THIS COMMENT'S FENCE — the JSX below is intact
              * and needs no reconstruction:
              *
              *   <span className="ml-auto flex shrink-0 items-baseline gap-2 text-xs text-muted-foreground">
              *       <span className="whitespace-nowrap">
              *           In progress <span className="font-semibold text-foreground">{data?.working ?? 0}</span>
              *       </span>
              *       <span className="whitespace-nowrap">
              *           Finished <span className="font-semibold text-foreground">{data?.done ?? 0}</span>
              *       </span>
              *   </span>
              *
              * ⛔⛔ AND `ml-auto` WENT WITH IT, WHICH IS NOT INCIDENTAL. That class is what
              * pushed the refresh icon to the right edge (see the note above about the icon
              * landing past "Finished 5"). With this span gone the icon loses its spacer, so
              * `ml-auto` MOVES TO THE NEXT ELEMENT rather than disappearing.
              *   ** REMOVING AN ELEMENT REMOVES WHATEVER LAYOUT IT WAS PROVIDING FOR ITS
              *      NEIGHBOURS. THE HOLE IS NOT WHERE YOU DELETED. **
              *
              * ⚠️ THE DATA IS STILL FETCHED — `data.working` / `data.done` come with the
              * rows and cost nothing extra. Only the render is suppressed, so turning it
              * back on is a display change and not a re-wiring.
              */}
            {/**
              * ---- ⭐ ONE COUNT, FAR RIGHT, LIKE THE OTHER RAILS (Pete, 2026-09-19) ----
              *
              * ⭐ *"Maybe we have the total labor count on the far right so it matches to
              * other panels"*.
              *
              * ⛔ COPIED FROM `OrdersPanel` / `CommunicationsPanel` DELIBERATELY — same
              * element, same classes, same position: `ml-auto shrink-0 text-xs
              * text-muted-foreground`, immediately before the refresh icon. Three rails in
              * one column that put their counts in three places is the screen Pete has
              * asked to be rid of twice.
              *
              * ⛔⛔ AND IT IS `inScope`, NOT `sessions`. The panel is scoped; a total over
              * the whole day sitting under a `(client)` label would be a number describing
              * a different set from the rows beneath it — the exact fault just fixed in the
              * check-in header (`In 4 · Out 1` over one client's two loads).
              *   ** A COUNT IN A PANEL HEADER DESCRIBES WHAT IS IN THAT PANEL. **
              *
              * ⚠️ HIDDEN WHILE LOADING, so the refresh icon takes `ml-auto` in that state —
              * without the swap the icon jumps left for the duration of every fetch.
              */}
            {!loading && (
                <span
                    className="ml-auto shrink-0 text-xs text-muted-foreground"
                    title={focus ? `Labor sessions for ${focusBasisLabel(focus)}` : "Today's labor sessions"}
                >
                    {inScope.length}
                </span>
            )}
            {/* ⚠️ OUTSIDE the collapse toggle, so it can be clicked without folding
                the panel — and it re-reads the day rather than the whole rail.
                ⛔ `ml-auto` INHERITED from the suppressed counts span above — without it
                the refresh icon sits against the panel name instead of the right edge,
                out of line with Communications and Driver Check-ins. */}
            <button
                type="button"
                onClick={() => { void load(); }}
                disabled={loading}
                className={`rounded p-0.5 hover:bg-muted disabled:opacity-40 ${loading ? 'ml-auto' : ''}`}
                title="Re-read today's labor now"
            >
                <RefreshCw className="h-3 w-3" />
            </button>
            </div>

            {open && (
                /* ⚠️ NO max-h HERE — the rail gives this section its own share of
                   the column (#283). A second ceiling inside it would produce a
                   scrollbar within a scrollbar.
                   ⭐ AND THE SCROLLER IS NOW HERE rather than around the whole panel,
                   so the header above cannot travel with it. See the header note. */
                <div className="min-h-0 overflow-y-auto px-2 pb-2">
                    {error && (
                        <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs">
                            Couldn’t read labor — {error}
                        </div>
                    )}

                    {/**
                      * ⛔ THE FACILITY COULD NOT BE RESOLVED — NOT THE SAME AS A QUIET DAY.
                      *
                      * ⚠️ This returned a silent empty day until 2026-08-27, so an unknown
                      * or renamed facility code looked exactly like a dock with nothing on
                      * it. Pete: *"labor is not showing for a task in-progress."*
                      */}
                    {!error && data?.unavailable && (
                        <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                            {data.unavailable}
                        </div>
                    )}

                    {!error && !data?.unavailable && sessions.length === 0 && !loading && (
                        <div className="px-1 py-2 text-xs text-muted-foreground">
                            Nobody has clocked on to a truck today.
                            {/**
                              * ⭐⭐ AND IF SOMEBODY IS WORKING SOMEWHERE ELSE, SAY SO.
                              *
                              * ⛔ THIS IS THE LINE THAT TELLS THE TWO FAULTS APART. A zero
                              * here means a quiet floor. A number here means sessions ARE
                              * open — just not under the facility code this screen is
                              * asking for — which is a mismatch between what Dock sends and
                              * what Pipeline wrote, and it is a five-minute fix once seen.
                              */}
                            {(data?.elsewhere ?? 0) > 0 && (
                                <span className="mt-1 block text-amber-800">
                                    ⚠️ {data!.elsewhere} session
                                    {data!.elsewhere === 1 ? ' is' : 's are'} open right now at a
                                    different facility code. If work IS happening here, the code
                                    on this screen and the one Pipeline is writing do not match.
                                </span>
                            )}
                        </div>
                    )}

                    {/**
                      * ⭐⭐ THE FILTER ROW (2026-08-27) — a door picker and one text box.
                      *
                      * ⚠️ SHOWN ONLY WHEN THERE IS SOMETHING TO NARROW. A filter row above
                      * three sessions is chrome on a rail that has been fighting for height
                      * all day (#310, #318); below the threshold the list IS the answer.
                      *
                      * ⛔ AND THE DOOR PICKER IS HIDDEN WHEN EVERY SESSION IS ON ONE DOOR.
                      * A dropdown with a single option is a control that cannot change
                      * anything, which reads as a control that is broken.
                      */}
                    {/* ⛔ BOTH CONDITIONS. The threshold is about whether the row is
                        WORTH anything; the toggle is about whether it is asked for. */}
                    {showFilters && sessions.length > 3 && (
                        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                            {doors.length > 1 && (
                                <select
                                    value={doorFilter}
                                    onChange={(e) => setDoorFilter(e.target.value)}
                                    aria-label="Filter by dock door"
                                    className="min-w-0 rounded border bg-background px-1 py-0.5 text-[11px]"
                                >
                                    <option value="">All doors</option>
                                    {doors.map(d => <option key={d} value={d}>{d}</option>)}
                                    {/**
                                      * ⚠️ A DOOR THAT HAS DROPPED OUT OF TODAY'S SESSIONS STILL
                                      * APPEARS WHILE IT IS SELECTED. A <select> whose value is
                                      * not among its options silently shows the FIRST one — so
                                      * the box would read "All doors" while the list stayed
                                      * filtered to a door nobody can see. Same trap the
                                      * check-in panel's client filter documents.
                                      */}
                                    {doorFilter !== '' && !doors.includes(doorFilter) && (
                                        <option value={doorFilter}>{doorFilter} (no sessions now)</option>
                                    )}
                                </select>
                            )}
                            <input
                                type="search"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Worker, load, client…"
                                aria-label="Filter labor sessions"
                                className="min-w-0 flex-1 rounded border bg-background px-1.5 py-0.5 text-[11px]"
                            />
                        </div>
                    )}

                    {/**
                      * ⛔ A FILTERED LIST SAYS WHAT IT IS HIDING. "In progress 4" in the
                      * header beside two visible rows is a contradiction a supervisor has
                      * to resolve by guessing, and the header count is deliberately NOT
                      * filtered — it is the honest total for the day.
                      */}
                    {/* ⭐ THE SCOPE, AND WHAT IT MATCHED ON. An unmatched driver gets a
                        SENTENCE, not an empty list — labour is tracked against the appointment and
                        `workerName` is the warehouse worker, never the driver. */}
                    {/* ⛔⛤ ~~THE GREY SCOPE BOX~~ REMOVED 2026-09-19 (§0.3). Pete: *"I don't
                            like the gray box inside the panel and then the same small text line
                            underneath it saying the same thing."*
                            ⚠️ HE WAS DESCRIBING A REAL DUPLICATION, NOT A PREFERENCE: this panel
                            said the date THREE times — "Showing <date>" above, "an order outside
                            <date> would not be here" in the box, and "Nothing due <date>" below.
                            ** A BOX THAT RESTATES ITS NEIGHBOURS IS NOT CONTEXT, IT IS NOISE —
                               AND THE READER STOPS READING ALL THREE. **
                            ✅ The one thing only the box carried — the BLOCKED REASON — moves to a
                            plain line, because it is the sentence that stops a scheduler reading an
                            empty panel as "nothing was sent". ⛔ It must not be lost with the
                            chrome. */}
                        {blockedReason && (
                            <div className="mb-1.5 text-[11px] text-muted-foreground">{blockedReason}</div>
                        )}
                    
                    {/* ⛔ ITS OWN LINE, NOT ADDED TO `hidden`. The board's filters are
                        cleared on the board, so a "Clear" beside this number would be a
                        control that cannot do what it says. */}
                    {boardScopeNote(boardHidden) && (
                        <div className="mb-1.5 text-[11px] text-muted-foreground">
                            {boardScopeNote(boardHidden)}
                        </div>
                    )}
                    {hidden > 0 && (
                        <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                            <span>{hidden} hidden by the filter.</span>
                            <button
                                type="button"
                                onClick={() => { setDoorFilter(''); setFilter(''); }}
                                className="underline"
                            >
                                Clear
                            </button>
                        </div>
                    )}

                    {visible.length === 0 && sessions.length > 0 && (
                        <div className="mb-1.5 rounded border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground">
                            Nothing matches that. {sessions.length} session
                            {sessions.length === 1 ? '' : 's'} today.
                        </div>
                    )}

                    <ul className="space-y-1">
                        {visible.map(s => (
                            <LabourRow
                                key={s.labourSessionId}
                                s={s}
                                nowMs={now}
                                onHoverAppointment={onHoverAppointment}
                                onChooseAppointment={onChooseAppointment}
                                clickIntent={clickIntent}
                                onOpenCorrection={setCorrecting}
                            />
                        ))}
                    </ul>
                    {/* ⭐ F131 — said, not hidden: without the corrections read, a
                        corrected record shows as the kiosk made it. */}
                    {data?.correctionsUnavailable && (
                        <div className="mt-1.5 text-[11px] text-amber-700">{data.correctionsUnavailable}</div>
                    )}
                </div>
            )}
            <LabourCorrectionDialog
                open={correcting !== null}
                facilityCode={facilityCode}
                session={correcting}
                getToken={getToken}
                onClose={() => setCorrecting(null)}
                onSaved={() => { setCorrecting(null); void load(); }}
            />
        </div>
    );
}

function LabourRow({
    s, nowMs, onHoverAppointment, onChooseAppointment, clickIntent, onOpenCorrection,
}: {
    s: LabourSessionDto;
    nowMs: number;
    /** ⭐ F131 — the panel's click intent, so one wait covers every row. */
    clickIntent: ClickIntent;
    /** ⭐ F131 — double-click opens the correction screen for this record. */
    onOpenCorrection: (s: LabourSessionDto) => void;
    /**
     * ⛔ THE SECOND ARGUMENT IS THE POINT. A rail row and a grid chip both report an
     * appointment hover, and the page must treat them oppositely: a grid chip
     * populates the panels, a RAIL row only marks the board. Without an origin the
     * panels re-scope, which replaces the very row the cursor is on.
     *   ** A HOVER THAT RE-SCOPES THE LIST CONTAINING THE HOVERED ROW IS A LOOP BY
     *      CONSTRUCTION. ** (Pete, 2026-09-21)
     */
    onHoverAppointment?: (id: string | null, origin?: 'grid' | 'rail') => void;
    /** ⭐ Single click latches the scope — see the prop on the panel. */
    onChooseAppointment?: (id: string | null) => void;
}) {
    /**
     * ---- ⛔⛔⛤⛤ `s.state` HAS TWO VALUES AND THE DOMAIN HAS FOUR ------------
     *
     * > Pete, 2026-09-24: *"Paused on the kiosk, not anywhere esle."*
     *
     * ⛔ THIS ROW SPLIT ON `s.state === 'working'`, which means "the ASSIGNMENT is
     * open". Since migration 201 that is not the same as "somebody is working it":
     * pausing stops the clock and leaves the assignment open. So a paused task
     * rendered emerald, with a spinning truck, reading "In progress", counting
     * wall-clock. Every one of those four was wrong at once.
     *
     * ⚠️ AND THE SERVER HAD BEEN SENDING THE ANSWER SINCE 2026-09-14.
     * `labourRepository.ts` carries a comment saying this panel would read
     * `assignmentState` "and then it goes" — but `LabourSessionDto` in
     * `api/client.ts` is a HAND-MAINTAINED MIRROR that never declared the field,
     * so no component could.
     *   ** A TYPE MIRROR THAT LAGS THE SERVER HIDES THE FIELD AS COMPLETELY AS IF
     *      IT HAD NEVER BEEN SENT, AND THE COMPILER AGREES WITH THE MIRROR. **
     *
     * ⭐ `labourLook` IS THE ONE DERIVATION — shared with the floor board and the
     * appointment tiles (§1E), so the three surfaces cannot drift again.
     */
    const look = labourLook(s.assignmentState, s.breakKind, s.finishedUtc !== null);
    const working = look.tone === 'working';
    const paused = look.tone === 'paused';
    /**
     * ⭐ WORKED TIME, NOT WALL-CLOCK — Pete: *"Worked time, with a paused-since
     * line."* ⚠️ FALLS BACK to the old elapsed reading when `workedSeconds` is
     * absent, so an older backend degrades to yesterday's number rather than to a
     * confident zero. ** A NULL IS AN HONEST GAP. A ZERO IS A CLAIM. **
     */
    const worked = workedLabel(s.workedSeconds);
    const pausedAt = agoLabel(pausedSince(s.assignmentState, s.lastIntervalEndedUtc), nowMs);

    /**
     * ---- ⭐ F131 — WHAT THE ROW SHOWS WHEN A SCHEDULER CORRECTED IT ------------
     *
     * ⛔ THE CORRECTED VALUES, MARKED, WITH THE RECORD BEHIND THEM. The row reads
     * the correction's worker, task, times and pallets; the "Corrected" mark's
     * tooltip says what the kiosk recorded, who changed it, when and why.
     * ⚠️ WORKED TIME IS STILL THE KIOSK'S — it is summed from intervals, and a
     * correction moves the start and end, not the pauses inside them. When the
     * record is corrected the row shows start-to-finish instead, and says so.
     */
    const c = s.correction ?? null;
    const shownWorker = c ? c.workerName : s.workerName;
    const shownTask = c ? c.taskLabel : s.taskLabel;
    const shownStarted = c ? c.startedUtc : s.startedUtc;
    const shownFinished = c ? c.finishedUtc : s.finishedUtc;
    const correctedTitle = c
        ? `Corrected${c.correctionCount > 1 ? ` (${c.correctionCount} times)` : ''} by ${c.correctedBy}, `
          + `${new Date(c.correctedUtc).toLocaleString()} — "${c.reason}".\n`
          + `As recorded: ${s.workerName ?? 'Unnamed worker'} · ${s.taskLabel ?? 'no task'} · `
          + `${hhmm(s.startedUtc)}${s.finishedUtc ? ` → ${hhmm(s.finishedUtc)}` : ' → running'}.`
        : undefined;
    const rowKey = `labor:${s.labourSessionId}`;

    /**
     * ---- ⭐⭐ THE HOVER BORDER (Pete, 2026-09-23) -------------------------
     *
     * > *"the highlight around a card in labor and comms is still not working. When
     * > you mouse over it, it doesn't have a border like it does in scanned
     * > paperwork"* — then, narrowed: *"just looks like Labor has the highlight
     * > issue… It should still border it on hover even if it doesn't have an
     * > appointment assigned."*
     *
     * ⛔⛤ THIS ROW HAD NO `hover:` CLASS AT ALL. Not a conditional one, not a
     * broken one — none. CommunicationsPanel's row has carried
     * `transition-colors hover:border-primary/50 hover:bg-primary/5` since the rail
     * was built; this file never got that half, so there was nothing to debug and
     * nothing to see.
     *
     * ---- ⛔⛔ AND HIS SECOND SENTENCE NAMES THE REAL CONFUSION ----------------
     *
     * `onMouseEnter` below is guarded on `s.appointmentId`, and it SHOULD be — a
     * row with no load has no appointment to mark on the board. But that guard is
     * about REPORTING a hover; the border is about SHOWING one. Two mechanisms, and
     * only one of them may be choosy.
     *   ** THE HOVER THAT MARKS AND THE HOVER THAT REPORTS ARE DIFFERENT THINGS.
     *      ONE IS CSS AND APPLIES TO EVERY ROW; THE OTHER IS A HANDLER. **
     * ⭐ CSS cannot see `appointmentId`, so putting the border in the class list
     * answers both halves of his report at once: every row responds, load or no
     * load.
     *
     * ---- ⚠️ WHY IT BRIGHTENS RATHER THAN RECOLOURS -------------------------
     *
     * A WORKING row is emerald because somebody is on that door right now. A flat
     * `hover:border-primary/50` would turn that green row blue under the cursor —
     * the state colour changing as the mouse passes over it.
     *   ** A HOVER THAT REPLACES A STATE COLOUR MAKES THE STATE LOOK LIKE IT MOVED.
     *      BRIGHTEN, DO NOT RECOLOUR. **
     * ⭐ So each state keeps its hue and gains contrast: emerald goes to full
     * emerald, the neutral row goes to primary exactly like Communications.
     */
    return (
        <li
            className={
                'rounded border px-2 py-1.5 text-xs transition-colors '
                + (working
                    ? 'border-emerald-500/50 bg-emerald-500/5 hover:border-emerald-500 hover:bg-emerald-500/10'
                    : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-primary/5')
            }
            /* ⛔ STILL GUARDED, AND STILL RIGHT. A row with no appointment has
               nothing to mark on the board; the BORDER above is what tells the
               scheduler which row the cursor is on, and it needs no load. */
            onMouseEnter={() => s.appointmentId && onHoverAppointment?.(s.appointmentId, 'rail')}
            onMouseLeave={() => onHoverAppointment?.(null, 'rail')}
            /* ⭐⭐ SINGLE CLICK MAKES THE HOVER STAY (Pete, 2026-09-27).
               ⛔ GUARDED ON THE ID, exactly as the hover above is and for the same
               reason: a session with no load has no appointment to scope TO, and
               latching null would silently clear whatever the scheduler had chosen.
                 ** A CONTROL THAT CANNOT ACT MUST DO NOTHING, NOT UNDO. **
               ⚠️ NO `stopPropagation` — this row is not inside another click target,
               and adding one pre-emptively hides the day somebody nests it. */
            onClick={() => clickIntent.click(
                rowKey,
                () => { if (s.appointmentId) onChooseAppointment?.(s.appointmentId); },
                () => onOpenCorrection(s),
            )}
            /* ⭐ F131 — the browser's own double-click, for a second click the wait
               missed; `dbl` will not open the screen twice. */
            onDoubleClick={() => clickIntent.dbl(rowKey, () => onOpenCorrection(s))}
            title="Double-click to correct this record"
        >
            {/**
              * ---- ⭐ LINE 1: THE DOOR, THEN WHO IS ON IT ----------------------
              *
              * Pete: *"Dock Door needs to be on the top in bold with a dash after and
              * the labor name."* The door is the thing being scanned down the column;
              * the worker qualifies it.
              */}
            <div className="flex items-baseline justify-between gap-2">
                <div className="flex min-w-0 items-baseline gap-1.5">
                    {/**
                      * ⚠️ RENDERED EVEN WHEN NULL. "no door" is a real and interesting
                      * state — somebody is clocked onto a truck that is not at a door —
                      * and blanking the slot would both ragged the column and hide it.
                      */}
                    <span className={'shrink-0 font-bold uppercase tracking-wide '
                        + (s.doorName ? '' : 'text-muted-foreground')}>
                        {s.doorName ?? 'no door'}
                    </span>
                    <span className="shrink-0 text-muted-foreground">—</span>
                    <span className="truncate">{shownWorker ?? 'Unnamed worker'}</span>
                    {c && (
                        <span className="shrink-0 rounded bg-sky-500/15 px-1 text-[10px] font-semibold uppercase tracking-wide text-sky-700"
                              title={correctedTitle}>
                            Corrected
                        </span>
                    )}
                </div>

                {/**
                  * ⭐ ELAPSED IS THE HEADLINE NUMBER, and #149 turns it red once it
                  * passes the standard from #142. Pete: *"Maybe make the Elapsed time
                  * Red or something."*
                  * ⛔ NO COLOUR TODAY — there is no standard to compare against yet, and
                  * a red that means nothing is worse than no red at all.
                  */}
                {/**
                  * ⛔⛔ THIS NUMBER USED TO KEEP COUNTING THROUGH A PAUSE. It was
                  * wall-clock since the assignment opened, so a task put down at
                  * 10:00 and picked up at 14:00 reported four hours of labour
                  * nobody performed — and that number lands in job costing.
                  *   ** AN ELAPSED CLOCK MEASURES THE WORLD. A WORKED CLOCK
                  *      MEASURES THE WORK. ONLY ONE OF THEM IS PAYROLL. **
                  * ⚠️ `worked` IS null WHEN THE FIELD DID NOT ARRIVE, and the old
                  * reading is the fallback — never a zero.
                  */}
                <span className={'shrink-0 tabular-nums ' + (working ? 'font-semibold' : 'text-muted-foreground')}
                      title={c ? 'Start to finish, as corrected — pauses are not corrected'
                             : worked !== null ? 'Worked time — breaks and pauses excluded' : undefined}>
                    {c ? elapsedLabel(shownStarted, shownFinished, nowMs)
                       : (worked ?? elapsedLabel(s.startedUtc, s.finishedUtc, nowMs))}
                </span>
                <DoorCameraPlaceholder doorName={s.doorName} />
            </div>

            {/**
              * ---- ⭐ LINE 2: THE STATE, SAID IN ONE GLANCE -------------------
              *
              * Pete: *"Maybe that Truck spinning icon in the upper right corner when
              * in-progress and a completed symbol or highlighted and bold Finished."*
              *
              * ⭐ THE SPINNING TRUCK IS THE APPLICATION'S EXISTING "BEING WORKED" MARK
              * (WorkTruckMark, arch §1E) — the same glyph the dockview board shows on a
              * door and the same one the grid puts on a chip. ⛔ A NEW ICON HERE WOULD
              * BE A SECOND SYMBOL FOR ONE FACT, which is precisely what that shared
              * component exists to prevent: a scheduler and a loader discussing the
              * same truck must be looking at the same mark.
              *
              * ⚠️ IT IS INLINE, NOT IN THE CORNER. Pete suggested the upper right; that
              * position already holds the elapsed time, which is the number he asked
              * for first (#149) and the one that will turn red. Putting the mark beside
              * the state word keeps both, and keeps the motion next to the word it
              * modifies.
              */}
            <div className="mt-0.5 flex items-baseline gap-1.5">
                {/**
                  * ⭐⭐ THREE GLYPHS, THREE WORDS, ONE SLOT (2026-09-24).
                  *
                  * > Pete: *"We need to have the univerasl Pause Symbol on the
                  * > appointment and ont the labor panel. Two vertical lines"* →
                  * > *"Put that smbol where ever it belongs on Pause in the floor
                  * > board, laborclock, dock schdule or labor panel."*
                  *
                  * ⭐ `PauseMark` SHARES WorkTruckMark's FOOTPRINT exactly — same
                  * viewBox, same circle radius — so the row does not shift when a
                  * task is paused. ** A STATE CHANGE THAT ALSO CHANGES THE LAYOUT
                  * IS TWO CHANGES, AND THE EYE ATTENDS TO THE WRONG ONE. **
                  *
                  * ⚠️ AMBER, NOT EMERALD, AND NOT RED. Emerald is "somebody is on
                  * it"; red on this panel is reserved for #149's overdue elapsed
                  * time, and a paused task is not late — it is put down.
                  *
                  * ⛔ THE MARK DOES NOT MOVE. WorkTruckMark spins because motion is
                  * what it MEANS; the whole content of the pause mark is that the
                  * motion stopped, so an animation here would contradict the word
                  * beside it.
                  */}
                {working ? (
                    <>
                        <WorkTruckMark
                            wrapClassName="relative inline-flex h-3.5 w-3.5 shrink-0 self-center text-emerald-700"
                            ringClassName="absolute inset-0 h-full w-full animate-spin motion-reduce:[animation-duration:8s]"
                            truckClassName="absolute left-[23%] top-[23%] h-[54%] w-[54%]"
                            ringStyle={{ animationDuration: `${WORK_MARK_SPIN_SECONDS}s` }}
                            ringStrokeWidth={4.5}
                            truckStrokeWidth={2.6}
                            title={look.title}
                        />
                        <span className="font-semibold text-emerald-700">{look.label}</span>
                    </>
                ) : paused ? (
                    <>
                        <PauseMark
                            wrapClassName="relative inline-flex h-3.5 w-3.5 shrink-0 self-center text-amber-600"
                            markClassName="absolute inset-0 h-full w-full"
                            ringStrokeWidth={4.5}
                            title={look.title}
                        />
                        {/* ⭐ THE WORD CARRIES THE BREAK KIND — PAUSED / ON BREAK /
                            ON MEAL. Pete chose that: *"Four states, break kind
                            named."* One man is back in ten minutes and the other has
                            moved to another truck, and a supervisor plans differently
                            for each. */}
                        <span className="font-semibold text-amber-600">{look.label}</span>
                        {/* ⚠️ ELAPSED, NEVER A CLOCK TIME — this panel may be read on a
                            machine in another timezone. */}
                        {pausedAt && (
                            <span className="text-muted-foreground">{pausedAt}</span>
                        )}
                    </>
                ) : (
                    <>
                        <Check className="h-3.5 w-3.5 shrink-0 self-center" />
                        <span className="font-semibold">{look.label}</span>
                    </>
                )}

                <span className="truncate text-muted-foreground">
                    {/* ⛔⛤ ~~'No load matched'~~ — 2026-09-24. This falls back when a
                        labour session has no APPOINTMENT attached: there is no client and
                        no carrier to name because nothing has been booked against it.
                        ⚠️ "Order" WOULD ALSO HAVE BEEN WRONG. Under the vocabulary Pete
                        chose today — Truck / Appointment / Order — an order is the
                        Camelot or Deposco record. A session is not missing one of those;
                        it is missing a BOOKING. "Load" carried both meanings, which is
                        exactly why it was retired.
                          ** RENAMING AN AMBIGUOUS WORD IS ONLY AN IMPROVEMENT IF THE NEW
                             ONE NAMES THE RIGHT OBJECT. ** */}
                    {/**
                      * ---- ⛔⛔ THE FALLBACK WAS SAYING SOMETHING FALSE (Pete, 2026-09-25)
                      *
                      * > *"Why do these all say no appointment matched but when I hover
                      * > them, they highlight an appointment?"*
                      *
                      * ⛔ BECAUSE THE LABEL AND THE HOVER ASK DIFFERENT QUESTIONS. The hover
                      * at onMouseEnter is guarded on `s.appointmentId` - an ID, present,
                      * which is why the grid lights up. This line never looked at the ID. It
                      * fell through on two NAMES being empty and reported that as the absence
                      * of an APPOINTMENT.
                      *
                      * ⚠️ THE NOTE ABOVE ASSERTED THE ASSUMPTION IN SO MANY WORDS: "there is
                      * no client and no carrier to name because nothing has been booked
                      * against it". That is untrue of a TBD load - carrier not known yet, no
                      * client on the booking - which is most of the rail in Pete's screenshot.
                      * The comment was the bug, written down.
                      *   ** A FALLBACK THAT NAMES A DIFFERENT FACT THAN THE ONE IT TESTED IS
                      *      A LIE WITH A TRUE CONDITION. **
                      *
                      * ⭐ FOUR CASES, FOUR HONEST ANSWERS: a client, else a carrier, else the
                      * appointment's own number, else - and only when there is genuinely no
                      * appointmentId - the original sentence, which is now true whenever it
                      * appears.
                      */}
                    {s.clientName || s.carrierName || s.appointmentNumber
                        || (s.appointmentId
                                ? 'Appointment with no client or carrier named'
                                : 'No appointment matched')}
                    {c?.palletsAtFinish != null ? ` · ${c.palletsAtFinish} plt at finish`
                        : s.palletCount != null ? ` · ${s.palletCount} plt` : ''}
                    {shownTask ? ` · ${shownTask}` : ''}
                </span>
            </div>

            {/**
              * ⛔ "STARTED" IS THE LABOR CLOCK, NOT THE TRUCK'S ARRIVAL — the
              * distinction Pete drew for #149: *"so have Truck arrival time and Unload
              * Start Time."* A truck can arrive at 2:26 and wait for a door until 2:40;
              * measuring a laborer from the arrival blames him for the wait. This time
              * comes from dbo.LaborSession.StartedUtc and nothing else.
              */}
            <div className="text-[11px] text-muted-foreground">
                Started {hhmm(shownStarted)}
                {shownFinished && (
                    <>
                        {' · Finished '}{hhmm(shownFinished)}
                        {/**
                          * ⚠️ THE CLOSE REASON IS SHOWN WHEN IT IS NOT A PERSON CLOCKING
                          * OFF. Pipeline auto-closes abandoned sessions (migration 032),
                          * and an auto-closed session's duration is not a measurement of
                          * work — #117 exists because reports were treating it as one.
                          */}
                        {s.closeReason && s.closeReason !== 'Manual' ? ` (${s.closeReason})` : ''}
                    </>
                )}
            </div>
        </li>
    );
}


/**
 * ---- ⭐ THE CAMERA THAT IS NOT WIRED UP YET --------------------------------
 *
 * Pete, 2026-08-25: *"we are going to integrate camera's into this app eventually. Can
 * you put a small camera icon greyed out on the right of the labor slider per Dock Door
 * and put one on each Dock Door in the left hand column... I want to show the
 * placeholder now."*
 *
 * ⛔ IT IS DISABLED, AND IT SAYS WHY — that is the entire difference between a
 * placeholder and a bug. A greyed icon with no explanation reads as "this is broken for
 * me", and the person it reads that way to is the scheduler who then rings IT. The
 * tooltip and the aria-label both say the cameras are not connected yet.
 *
 * ⚠️ A BUTTON, NOT A SPAN. A decorative icon is invisible to a screen reader and
 * unreachable by keyboard, so the day this gets wired the control would have to be
 * rebuilt as something else — and the "temporary" span outlives everyone's memory of
 * why it was a span.
 *
 * ⛔ AND IT IS `aria-disabled`, NOT `disabled` — THIS PARAGRAPH USED TO SAY THE
 * OPPOSITE. It shipped genuinely disabled, and then Pete asked for a hover viewer:
 * *"When you hover over the camera icon, a viewer screen... should show up, but
 * obviously blank now."* A disabled button receives NO POINTER EVENTS in any browser,
 * so `onMouseEnter` would never fire and the icon would look right while being dead.
 * ⚠️ A stale invariant in a header is worse than none, because the next person builds
 * on it — the same correction sharePointRepository.ts needed the same day.
 *
 * ⛔ ONE COMPONENT, TWO PLACES. The labour card and the grid's door column both use
 * this, so the mark for "there is a camera here" cannot drift into two marks — the same
 * rule WorkTruckMark exists for.
 */
export function DoorCameraPlaceholder(
    { doorName, variant = 'door' }: {
        doorName?: string | null;
        /**
         * ---- ⭐ TWO DIFFERENT PROMISES, SO TWO DIFFERENT MARKS ------------------
         *
         * Pete, 2026-08-25: *"In the Unassigned Queue - the camera icon, lets make it
         * look a tad different - this will be a view of all the cameras in the
         * warehouse in a grid."*
         *
         * ⛔ 'door' OPENS ONE FEED; 'wall' OPENS ALL OF THEM. An icon that looks the
         * same in both places promises the same thing in both places, and a scheduler
         * who clicks the unassigned row expecting one door's camera has been misled by
         * the interface rather than by anything they did.
         *
         * ⚠️ SAME COMPONENT, THOUGH. The variant changes the glyph and what the
         * preview draws — not the wiring, the placement, or the "not connected yet"
         * honesty. When the real cameras arrive, one file gets the feed.
         */
        variant?: 'door' | 'wall';
    },
) {
    const isWall = variant === 'wall';
    const ref = useRef<HTMLButtonElement>(null);
    const [box, setBox] = useState<{ left: number; top: number } | null>(null);
    const timer = useRef<number | null>(null);

    const label = isWall ? 'All cameras' : (doorName ?? 'this door');

    /**
     * ⚠️ A SHORT DELAY IN, NONE OUT. Brighton has 23 doors in one column; sweeping the
     * pointer down it would otherwise flash 23 panels. Leaving is instant because a
     * viewer that lingers over the thing you moved on to read is worse than no viewer.
     */
    const show = () => {
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
            const el = ref.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            // Beside the icon, flipped to the left when there is no room to the right,
            // and never pushed off the bottom of a short window.
            const w = isWall ? WALL_W : PREVIEW_W;
            const h = isWall ? WALL_H : PREVIEW_H;
            const left = r.right + GAP + w > window.innerWidth
                ? Math.max(GAP, r.left - w - GAP)
                : r.right + GAP;
            const top = Math.max(GAP, Math.min(r.top - 8, window.innerHeight - h - GAP));
            setBox({ left, top });
        }, 250);
    };

    const hide = () => {
        if (timer.current) window.clearTimeout(timer.current);
        setBox(null);
    };

    useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

    return (
        <>
            {/**
              * ⛔ `aria-disabled`, NOT `disabled`, AND THAT IS NOT A STYLE CHOICE.
              * A genuinely disabled button receives NO pointer events in any browser —
              * mouseenter never fires — so the hover Pete asked for would silently do
              * nothing, and the icon would look correct while being dead.
              *
              * ⚠️ SO IT MUST REFUSE THE CLICK ITSELF. aria-disabled is a promise to
              * assistive technology, not an enforcement; without preventDefault this
              * would bubble into "edit this door" in the grid's door column.
              */}
            <button
                ref={ref}
                type="button"
                aria-disabled="true"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                title={isWall
                    ? 'All warehouse cameras \u2014 not connected yet'
                    : `Camera for ${label} \u2014 not connected yet`}
                aria-label={isWall
                    ? 'All warehouse cameras, not connected yet'
                    : `Camera for ${label}, not connected yet`}
                className="shrink-0 cursor-not-allowed rounded p-0.5 text-muted-foreground/40
                           hover:text-muted-foreground/70"
            >
                {/**
                  * ⭐ THE WALL MARK IS A CAMERA *ON* A GRID, not a bare grid.
                  *
                  * Pete, 2026-08-25: *"Lets put a camera with that grid icon so it makes
                  * visual sense."* ⛔ HE IS RIGHT AND THE FIRST VERSION WAS HALF AN IDEA:
                  * a grid alone says "several of something" without saying several of
                  * WHAT, and beside a column of camera icons it reads as an unrelated
                  * layout control. The camera says what; the grid says how many.
                  *
                  * ⚠️ THE BADGE CARRIES THE PANEL'S OWN BACKGROUND behind it. Two glyphs
                  * overlapping at 14px smear into a blob without something to separate
                  * them, and an icon that cannot be read is worse than the bare grid.
                  */
                }
                {isWall ? (
                    <span className="relative inline-flex h-3.5 w-3.5 items-center justify-center">
                        <Grid2x2 className="h-3.5 w-3.5" />
                        <Camera
                            className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-[2px] bg-background"
                            strokeWidth={2.5}
                        />
                    </span>
                ) : (
                    <Camera className="h-3.5 w-3.5" />
                )}
            </button>

            {box && createPortal(
                /**
                 * ---- ⭐ THE VIEWER, DELIBERATELY EMPTY -------------------------
                 *
                 * Pete, 2026-08-25: *"When you hover over the camera icon, a viewer
                 * screen (just like a doc viewer popup) should show up, but obviously
                 * blank now."*
                 *
                 * ⛔ IT SAYS WHY IT IS BLANK. A black rectangle on its own is
                 * indistinguishable from a camera that is connected and broken — which
                 * is the single most expensive way for a placeholder to be wrong, since
                 * somebody goes looking for a fault that does not exist.
                 *
                 * ⚠️ pointer-events-none: this follows the pointer around a column of
                 * doors, and a panel that could intercept the cursor would block the
                 * very icons it is meant to preview.
                 */
                <div
                    style={{
                        left: box.left, top: box.top,
                        width: isWall ? WALL_W : PREVIEW_W,
                        height: isWall ? WALL_H : PREVIEW_H,
                    }}
                    className="pointer-events-none fixed z-50 flex flex-col overflow-hidden rounded-lg
                               border bg-background shadow-2xl"
                >
                    <div className="flex items-center gap-1.5 border-b px-2 py-1 text-[11px] font-medium">
                        {/* ⭐ The header repeats the mark from the button, so the panel
                            is recognisably the thing that was hovered. */}
                        {isWall ? (
                            <span className="relative inline-flex h-3 w-3 items-center justify-center">
                                <Grid2x2 className="h-3 w-3" />
                                <Camera
                                    className="absolute -bottom-1 -right-1 h-2 w-2 rounded-[2px] bg-background"
                                    strokeWidth={2.5}
                                />
                            </span>
                        ) : (
                            <Camera className="h-3 w-3" />
                        )}
                        <span className="truncate">{isWall ? 'All cameras' : label}</span>
                        {/**
                          * ⭐ THE STATUS LIVES IN THE TITLE BAR — Pete, 2026-08-25: *"lets
                          * move the status 'No camera connected' to the top bar"*.
                          *
                          * ⚠️ IT WAS SITTING IN THE MIDDLE OF THE PICTURE, which is the one
                          * place it competes with the thing it is describing. A status
                          * belongs beside the name of what it is the status OF.
                          */}
                        <span className="ml-auto shrink-0 text-[10px] font-normal text-slate-400">
                            {isWall ? 'No cameras connected' : 'No camera connected'}
                        </span>
                    </div>

                    {isWall ? (
                        /**
                         * ⭐ THE GRID IS DRAWN EMPTY RATHER THAN DESCRIBED. Pete: *"this
                         * will be a view of all the cameras in the warehouse in a grid...
                         * Still pull up a small popup for now but that will change when we
                         * build it."* Showing the SHAPE of the thing is what makes a
                         * placeholder worth having — it is the difference between "a
                         * camera goes here" and "the wall view goes here".
                         *
                         * ⚠️ FOUR FIXED TILES, NOT A COUNT OF REAL DOORS. There is no
                         * camera configuration yet, so any number drawn would be invented;
                         * four says "several, in a grid" without claiming to know.
                         */
                        <div className="grid flex-1 grid-cols-2 gap-px bg-border p-px">
                            {[0, 1, 2, 3].map(i => (
                                <div key={i} className="flex items-center justify-center bg-slate-900">
                                    <VideoOff className="h-4 w-4 text-slate-700" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        /**
                          * ⭐ A REAL DOCK DOOR, NOT A GREY RECTANGLE. Pete supplied the
                          * photograph: *"insert this image of a dock door to give it a
                          * little fun."* It shows the SHAPE of what will appear here, which
                          * is the same reason the wall variant draws an empty grid — a
                          * placeholder that looks like the real thing is worth having; a
                          * dark box is not.
                          *
                          * ⛔ IT IS DIMMED AND CAPTIONED SO IT CANNOT BE MISTAKEN FOR A LIVE
                          * FEED. A photograph of a door at full brightness in a viewer
                          * labelled with a door number is exactly how somebody comes to
                          * believe they are watching that door.
                          *
                          * ⚠️ SERVED FROM /public, NOT IMPORTED. The bundle is already over
                          * 1.5 MB; a decorative placeholder has no business inside it.
                          */
                        <div className="relative flex-1 overflow-hidden bg-slate-900">
                            <img
                                src="/dock-door-placeholder.jpg"
                                alt=""
                                aria-hidden="true"
                                className="h-full w-full object-cover opacity-40 grayscale"
                            />
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                                <VideoOff className="h-6 w-6 text-slate-300 drop-shadow" />
                                <span className="rounded bg-slate-900/70 px-1.5 py-0.5 text-[10px] text-slate-200">
                                    Placeholder — not a live feed
                                </span>
                            </div>
                        </div>
                    )}

                    {/* ⛔ IT SAYS WHY IT IS BLANK, in both variants — a dark rectangle on
                        its own is indistinguishable from a camera that is connected and
                        broken, and somebody goes hunting a fault that does not exist. */}
                    <div className="border-t px-2 py-1 text-center text-[10px] text-muted-foreground">
                        {isWall
                            ? 'No cameras connected. The warehouse grid appears here once they are set up.'
                            : 'Live view appears here once cameras are set up for this site.'}
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}
